import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import InputContainer from '@RHEnvironmental/pattern-library/dist/js/components/InputContainer';
import InputLabel from '@RHEnvironmental/pattern-library/dist/js/components/InputLabel';
import Input from '@RHEnvironmental/pattern-library/dist/js/components/Input';
import InputGroup from '@RHEnvironmental/pattern-library/dist/js/components/InputGroup';
import Button from '@RHEnvironmental/pattern-library/dist/js/components/Button';

import InlineButtonPanel from '@RHEnvironmental/pattern-library/dist/js/components/InlineButtonPanel';
import Table from '@RHEnvironmental/pattern-library/dist/js/components/Table';
import { getClassNames } from '@RHEnvironmental/pattern-library/dist/js/services/stylingUtils';
import LoadingIndicator from '@RHEnvironmental/pattern-library/dist/js/components/LoadingIndicator';
import BodyText from '../../modules/BodyText';

import {
    clearAddresses,
    getAddressesByPostcode,
    setSelectedAddress,
} from '../../../actions/editPropertyDetails';
import {
    lookupAddressToAddress,
    lookupAddressToSingleLine,
} from '../../../services/ui/addresses';

const AddressLookup = ({
    onSave,
    onCancel,
    existingAddress,
    addressableId,
    addressableType,
}) => {
    const form = {
        initialValues: {
            postcode: '',
        },
        initialStatus: {
            errors: {},
        },
    };

    const dispatch = useDispatch();
    const { addresses, selectedAddress, isLoadingAddresses } = useSelector(
        (state) => state.pages.editPropertyDetails
    );

    const getRowClasses = (address) =>
        getClassNames('address-lookup-table__row', {
            'address-lookup-table__row--selected':
                selectedAddress?.uprn === address.uprn,
        });

    const onConfirmAddressChange = () => {
        const address = lookupAddressToAddress({
            lookupAddress: selectedAddress,
            existingAddress,
            addressableId,
            addressableType,
        });

        onSave(address);
    };

    const enterKeyHandler = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            dispatch(setSelectedAddress(null));
            dispatch(
                getAddressesByPostcode(
                    document.getElementById('postcode').value
                )
            );
        }
    };

    useEffect(() => {
        const postcodeInput = document.getElementById('postcode');
        postcodeInput.addEventListener('keypress', enterKeyHandler);
        return () => {
            postcodeInput.removeEventListener('keypress', enterKeyHandler);
            dispatch(clearAddresses());
        };
    }, []);

    return (
        <Formik
            onSubmit={(formValues) => {
                dispatch(setSelectedAddress(null));
                dispatch(getAddressesByPostcode(formValues.postcode));
            }}
            enableReinitialize
            initialValues={form.initialValues}
            initialErrors={form.initialStatus}
        >
            {({ values, handleChange, handleSubmit }) => (
                <form>
                    <InputContainer>
                        <InputLabel htmlFor="postcode" show>
                            Enter a postcode
                        </InputLabel>
                        <InputGroup layout="inline" className="flex">
                            <Input
                                id="postcode"
                                onChange={handleChange}
                                value={values.postcode}
                                aria-required="true"
                                name="postcode"
                                type="text"
                            />
                            <Button
                                disabled={!values.postcode}
                                onClick={handleSubmit}
                                className="ml-2"
                                style={{ 'min-width': 'auto' }}
                            >
                                Search
                            </Button>
                        </InputGroup>
                    </InputContainer>
                    {isLoadingAddresses ? (
                        <LoadingIndicator show />
                    ) : (
                        <>
                            {addresses && addresses.length > 1 && (
                                <Table
                                    hasHover={{ background: true }}
                                    className="table--full-width table--row-selectable address-lookup-table"
                                >
                                    <tbody>
                                        {addresses.map((address, i) => (
                                            <tr
                                                className={getRowClasses(
                                                    address
                                                )}
                                                key={i}
                                                onClick={() =>
                                                    dispatch(
                                                        setSelectedAddress(
                                                            address
                                                        )
                                                    )
                                                }
                                            >
                                                <td data-th="">
                                                    {lookupAddressToSingleLine(
                                                        address
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                            {addresses && addresses.length < 1 && (
                                <BodyText>
                                    No addresses found for {values.postcode}
                                </BodyText>
                            )}
                            {addresses && selectedAddress && (
                                <BodyText className="mt-4">
                                    {lookupAddressToSingleLine(selectedAddress)}
                                </BodyText>
                            )}
                        </>
                    )}
                    <InlineButtonPanel>
                        <Button
                            className="button--white"
                            onClick={() => onCancel()}
                        >
                            Cancel
                        </Button>
                        {addresses && selectedAddress && (
                            <Button onClick={() => onConfirmAddressChange()}>
                                Update address
                            </Button>
                        )}
                    </InlineButtonPanel>
                </form>
            )}
        </Formik>
    );
};

export default AddressLookup;
